/**
 * LockBadge - Zeigt den Sperrstatus eines Eintrags an
 * 
 * Props:
 * - lockInfo: Das Objekt aus dem Backend mit level, reason, locked_by, etc.
 * - size: 'sm' | 'md' (default: 'sm')
 * - showLabel: boolean - zeigt Text neben dem Icon (default: false)
 */
import { Lock, LockOpen, Ban, Unlock } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const LOCK_LEVELS = {
  open: {
    icon: null,
    color: null,
    label: null,
    tooltip: null,
  },
  soft: {
    icon: LockOpen,
    color: 'text-muted-foreground',
    bgColor: 'bg-muted/50',
    label: 'Eingereicht',
    tooltip: 'Eingereicht — Änderung nur mit Begründung',
  },
  hard: {
    icon: Lock,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10',
    label: 'Gesperrt',
    tooltip: null, // Wird aus reason übernommen
  },
  final: {
    icon: Ban,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-500/10',
    label: 'Final',
    tooltip: 'Bereits abgerechnet oder exportiert — nur Storno möglich',
  },
};

export const LockBadge = ({ lockInfo, size = 'sm', showLabel = false }) => {
  if (!lockInfo || lockInfo.level === 'open') {
    return null;
  }

  const config = LOCK_LEVELS[lockInfo.level];
  if (!config || !config.icon) {
    return null;
  }

  const Icon = config.icon;
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const tooltipText = lockInfo.level === 'hard' && lockInfo.reason 
    ? lockInfo.reason 
    : config.tooltip;

  // Wenn kein Tooltip nötig (sollte nicht vorkommen)
  if (!tooltipText) {
    return (
      <span className={`inline-flex items-center gap-1 ${config.color}`}>
        <Icon className={iconSize} />
        {showLabel && <span className="text-xs">{config.label}</span>}
      </span>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${config.bgColor} ${config.color} cursor-help`}
            data-testid={`lock-badge-${lockInfo.level}`}
          >
            <Icon className={iconSize} />
            {showLabel && <span className="text-xs font-medium">{config.label}</span>}
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="bg-muted border-border text-foreground max-w-xs"
        >
          <p className="text-sm">{tooltipText}</p>
          {lockInfo.level === 'hard' && lockInfo.can_request_unlock && (
            <p className="text-xs text-muted-foreground mt-1">
              Entsperrung kann beantragt werden
            </p>
          )}
          {lockInfo.unlock_request && lockInfo.unlock_request.status === 'approved' && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
              Entsperrung genehmigt bis {new Date(lockInfo.unlock_request.expires_at).toLocaleString('de-DE')}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * LockStatusIndicator - Größere Anzeige mit mehr Details
 */
export const LockStatusIndicator = ({ lockInfo, onRequestUnlock, onStorno, canStorno = false }) => {
  if (!lockInfo || lockInfo.level === 'open') {
    return null;
  }

  const config = LOCK_LEVELS[lockInfo.level];
  if (!config) return null;

  const Icon = config.icon || Lock;

  return (
    <div className={`p-3 rounded-lg border ${
      lockInfo.level === 'final' 
        ? 'border-red-500/30 bg-red-500/5' 
        : lockInfo.level === 'hard'
          ? 'border-amber-500/30 bg-amber-500/5'
          : 'border-border bg-muted/50'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${config.bgColor}`}>
          <Icon className={`w-5 h-5 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-medium ${config.color}`}>
            {lockInfo.level === 'final' 
              ? 'Eintrag ist endgültig gesperrt'
              : lockInfo.level === 'hard'
                ? 'Eintrag ist gesperrt'
                : 'Eintrag zur Genehmigung eingereicht'}
          </p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {lockInfo.reason || config.tooltip}
          </p>
          
          {/* Unlock request status */}
          {lockInfo.unlock_request && (
            <div className="mt-2 p-2 rounded bg-muted/50 border border-border">
              {lockInfo.unlock_request.status === 'pending' && (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  <Unlock className="w-4 h-4 inline mr-1" />
                  Entsperrungsantrag ausstehend
                </p>
              )}
              {lockInfo.unlock_request.status === 'approved' && (
                <p className="text-sm text-emerald-600 dark:text-emerald-400">
                  <Unlock className="w-4 h-4 inline mr-1" />
                  Entsperrung genehmigt bis {new Date(lockInfo.unlock_request.expires_at).toLocaleString('de-DE')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex gap-2 mt-3">
        {lockInfo.level === 'hard' && lockInfo.can_request_unlock && !lockInfo.unlock_request && onRequestUnlock && (
          <button
            onClick={onRequestUnlock}
            className="text-sm px-3 py-1.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
            data-testid="request-unlock-btn"
          >
            Entsperrung beantragen
          </button>
        )}
        {lockInfo.level === 'final' && canStorno && onStorno && (
          <button
            onClick={onStorno}
            className="text-sm px-3 py-1.5 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
            data-testid="storno-btn"
          >
            Storno erstellen
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * UnlockHintBanner - Hinweisbalken wenn Bearbeitung aufgrund einer Freigabe möglich ist
 */
export const UnlockHintBanner = ({ unlockRequest }) => {
  if (!unlockRequest || unlockRequest.status !== 'approved') {
    return null;
  }

  const expiresAt = new Date(unlockRequest.expires_at);
  const now = new Date();
  const remainingHours = Math.max(0, Math.round((expiresAt - now) / (1000 * 60 * 60)));

  return (
    <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 mb-4">
      <div className="flex items-start gap-3">
        <Unlock className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
            Bearbeitung aufgrund einer Freigabe möglich
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Gültig bis {expiresAt.toLocaleString('de-DE')} 
            {remainingHours > 0 && ` — noch ${remainingHours} Stunden`}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Die Woche wird nach dem Speichern erneut zur Genehmigung eingereicht.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * StornoBadge - Badge für Storno-Einträge
 */
export const StornoBadge = ({ entry, onViewOriginal }) => {
  if (!entry?.is_correction) {
    return null;
  }

  return (
    <div className="inline-flex items-center gap-1">
      <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-red-500/20 text-red-400 border border-red-500/30">
        Storno
      </span>
      {entry.corrects_document_id && onViewOriginal && (
        <button
          onClick={() => onViewOriginal(entry.corrects_document_id)}
          className="text-xs text-muted-foreground hover:text-foreground underline"
        >
          Originaleintrag
        </button>
      )}
    </div>
  );
};

/**
 * StornoHint - Hinweis wenn ein Eintrag storniert wurde
 */
export const StornoHint = ({ stornoEntry, stornoDate }) => {
  if (!stornoEntry) return null;

  return (
    <span className="text-xs text-red-600 dark:text-red-400">
      Storniert am {stornoDate ? new Date(stornoDate).toLocaleDateString('de-DE') : 'unbekannt'}
    </span>
  );
};

export default LockBadge;
